import {TileMediaSpec} from '../../MediaCard';
import {fetchFileBlob, getObjectURLForBlob} from './blob';
import IN_MEMORY_CACHE, {
  updateTileMediaStatus,
  updateTracking,
} from './inMemoryCache';
import {getKeyForRunSignature, getKeyForTileMediaSpec} from './keys';
import {PathSpec} from './types';

// Use all of the data we fetched to update the global cache.
// After this completes, the cache is ready for all Media Panels to use.
export async function updateWithNewData(
  tileMediaSpecsByRunSignatureKey: Map<string, TileMediaSpec[]>,
  existingFilePathByKey: Map<string, PathSpec>,
  directURLByRunFilePathOrArtifactURI: Map<string, string>,
  internalURLByRunFilePathOrArtifactURI: Map<string, string>
): Promise<void> {
  const getTileMediaPromises: Array<Promise<void>> = [];
  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    for (const tileMediaSpec of tileMediaSpecs) {
      getTileMediaPromises.push(fetchWrapper(tileMediaSpec));
    }
  }
  await Promise.all(getTileMediaPromises);

  async function fetchWrapper(tileMediaSpec: TileMediaSpec) {
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
    try {
      await fetchBlobAndSetTileMedia(tileMediaSpec);
    } catch (error) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorMessage:
          error instanceof Error ? error.message : 'Failed to fetch media',
      });
    }
  }

  async function fetchBlobAndSetTileMedia(
    tileMediaSpec: TileMediaSpec
  ): Promise<void> {
    const {runSignature} = tileMediaSpec;
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);

    const pathSpec = existingFilePathByKey.get(tileMediaSpecKey);
    if (pathSpec == null) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorMessage: 'No path specification found for media',
      });
      return;
    }

    const path =
      `filePath` in pathSpec ? pathSpec.filePath : pathSpec.artifactURI;
    const directURL = directURLByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    const internalURL = internalURLByRunFilePathOrArtifactURI.get(
      `${getKeyForRunSignature(runSignature)}-${path}`
    );
    if (directURL == null) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorMessage: 'No direct URL found for media',
      });
      return;
    }

    const details = {
      step: tileMediaSpec.mediaStep,
      index: tileMediaSpec.mediaIndex,
      historyRow: pathSpec.historyRow,
      filePath: path,
      directURL,
      internalURL,
      mediaSliderKey: tileMediaSpec.mediaSliderKey,
      mediaSliderKeyValue: tileMediaSpec.mediaSliderKeyValue,
    };

    /**
     * for images we can resize we skip the blob, this keeps us from downloading R.O.U.S. (pictuRes Of Unusual Size 🐀) directly from the Google bucket
     * */
    if (tileMediaSpec.mediaType === 'image' && internalURL != null) {
      const nullBlob = new Blob();

      IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.set(tileMediaSpecKey, {
        ...details,
        mediaKey: tileMediaSpec.mediaKey,
        blob: nullBlob,
        objectURL: getObjectURLForBlob(nullBlob),
      });
      updateTracking(tileMediaSpecKey, 'dataCachedMs');
      updateTileMediaStatus(tileMediaSpecKey, {
        isLoaded: true,
        hasError: false,
      });
      return;
    }

    try {
      const blob = await fetchFileBlob(directURL);
      if (blob == null) {
        updateTileMediaStatus(tileMediaSpecKey, {
          hasError: true,
          errorMessage: 'Failed to fetch blob data',
        });
        return;
      }
      IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.set(tileMediaSpecKey, {
        ...details,
        mediaKey: tileMediaSpec.mediaKey,
        blob,
        objectURL: getObjectURLForBlob(blob),
      });
      updateTracking(tileMediaSpecKey, 'dataCachedMs');
      updateTileMediaStatus(tileMediaSpecKey, {
        isLoaded: true,
        hasError: false,
      });
    } catch (e) {
      updateTileMediaStatus(tileMediaSpecKey, {
        hasError: true,
        errorMessage:
          e instanceof Error ? e.message : 'Failed to process media data',
      });
    }
  }
}
